import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MapPin } from "lucide-react";
import { PHONE_TEL, DIRECTIONS_URL } from "../../lib/site";

const lineReveal = (i) => ({
  initial: { y: "110%" },
  animate: { y: "0%" },
  transition: { duration: 0.75, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  return (
    <section id="top" ref={ref} className="relative bg-white pt-24 sm:pt-28 overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#0a0a0a 1px, transparent 1px), linear-gradient(90deg, #0a0a0a 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      <div className="absolute top-0 right-0 w-24 h-24 sm:w-40 sm:h-40 bg-red-700 pointer-events-none" style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 sm:pb-14">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-6 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <h1 className="font-heading text-zinc-950 text-[15vw] sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              <span className="block overflow-hidden">
                <motion.span className="block" {...lineReveal(0)}>SCRAP PAYS.</motion.span>
              </span>
              <span className="block overflow-hidden">
                <motion.span className="block text-red-700" {...lineReveal(1)}>WE PAY MORE.</motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-6 text-lg sm:text-xl text-zinc-600 max-w-xl font-medium"
            >
              Get top dollar for your scrap metal at Scrapman's Metal Recycling in Ashley, Ohio.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-8 flex flex-col sm:flex-row gap-4"
            >
              <a
                href={PHONE_TEL}
                className="inline-flex items-center justify-center gap-3 bg-red-700 text-white px-8 py-4 font-heading text-lg tracking-wider hover:bg-red-800 transition-colors shadow-[5px_5px_0px_0px_rgba(10,10,10,1)] hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:translate-x-[3px] hover:translate-y-[3px] duration-150"
                style={{ transitionProperty: "background-color, box-shadow, transform" }}
                data-testid="hero-call-cta"
              >
                <Phone className="h-5 w-5" />
                CALL FOR TODAY'S PRICE
              </a>
              <a
                href={DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 border-2 border-zinc-950 text-zinc-950 px-8 py-4 font-heading text-lg tracking-wider hover:bg-zinc-950 hover:text-white transition-colors"
                data-testid="hero-directions-cta"
              >
                <MapPin className="h-5 w-5" />
                GET DIRECTIONS
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2 flex justify-center lg:justify-end">
            <motion.img
              src="/scrapmans-logo.png"
              alt="Scrapman's Metal Recycling — Ashley, OH"
              className="w-56 sm:w-80 lg:w-full max-w-md"
              style={{ y: logoY }}
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-logo"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
